"use client";

import { useMarket } from "@app/_context";

import { formatPrice } from "@/utils/priceUtils";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@app/_components/Accordion/Accordion";
import DiscountForm from "@app/_components/Checkout/components/Discount/DiscountForm";
import EurobonusForm from "@app/_components/Checkout/components/Eurobonus/EurobonusForm";
import { useCheckoutContext, useCheckoutSummary } from "./Checkout.hooks";
import { shouldShowEurobonus } from "./Checkout.utils";

export default function CheckoutSummary() {
  const marketConfig = useMarket().state.market;
  const { dictionary } = useCheckoutContext();
  const {
    isLoading,
    cartTotal,
    cartSum,
    cartCost,
    cartVAT,
    cartShipping,
    cartDiscountAmount,
    cartDiscountCodeName,
    cartDiscountCodeAmount,
  } = useCheckoutSummary();

  if (!cartTotal) {
    return null;
  }

  return (
    <div className="flex flex-col px-5 py-6 lg:flex-row-reverse">
      <div className="flex-1 space-y-1">
        <div className="child:leading-6 flex justify-between">
          <p>{dictionary?.totalSumProducts}</p>
          {isLoading ? (
            <div className="mt-1 h-4 w-20 animate-pulse bg-gray-100" />
          ) : (
            <p>{formatPrice(cartSum, marketConfig.locale)}</p>
          )}
        </div>
        {cartDiscountAmount && cartDiscountAmount > 0 ? (
          <div className="child:leading-6 flex justify-between">
            <p>{dictionary?.youSave}</p>
            {isLoading ? (
              <div className="mt-1 h-4 w-20 animate-pulse bg-gray-100" />
            ) : (
              <p>-{formatPrice(cartDiscountAmount, marketConfig.locale)}</p>
            )}
          </div>
        ) : null}
        {cartDiscountCodeAmount && cartDiscountCodeAmount > 0 ? (
          <div className="child:leading-6 flex justify-between">
            <p>
              {dictionary?.discountCode ?? "Discount code"} (
              {cartDiscountCodeName})
            </p>
            {isLoading ? (
              <div className="mt-1 h-4 w-20 animate-pulse bg-gray-100" />
            ) : (
              <p>-{formatPrice(cartDiscountCodeAmount, marketConfig.locale)}</p>
            )}
          </div>
        ) : null}
        <div className="child:leading-6 flex justify-between">
          <p>{dictionary.shippingCost}</p>
          {isLoading ? (
            <div className="mt-1 h-4 w-20 animate-pulse bg-gray-100" />
          ) : (
            <p>{formatPrice(cartShipping, marketConfig.locale)}</p>
          )}
        </div>
        <div className="child:leading-6 flex justify-between">
          <p className=" ">{dictionary?.amountToPay}</p>
          {isLoading ? (
            <div className="mt-1 h-4 w-20 animate-pulse bg-gray-100" />
          ) : (
            <p className=" ">{formatPrice(cartCost, marketConfig.locale)}</p>
          )}
        </div>
        <div className="child:leading-6 flex justify-between">
          <p>{dictionary?.vat}</p>
          {isLoading ? (
            <div className="mt-1 h-4 w-20 animate-pulse bg-gray-100" />
          ) : (
            <p>{formatPrice(cartVAT, marketConfig.locale)}</p>
          )}
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-y-2 max-lg:mt-5 lg:mr-5">
        <Accordion type="multiple" defaultValue={[]}>
          <AccordionItem value="discount" className="border-0 pb-2">
            <AccordionTrigger className="justify-start gap-2 py-0 text-gray-500">
              {dictionary.discountCode}
            </AccordionTrigger>
            <AccordionContent className="pb-0">
              <DiscountForm dictionary={dictionary} />
            </AccordionContent>
          </AccordionItem>
          {shouldShowEurobonus(cartSum, marketConfig) && (
            <AccordionItem value="eurobonus" className="border-0 pb-2">
              <AccordionTrigger className="justify-start gap-2 py-0 text-gray-500">
                {dictionary.eurobonusNumber}
              </AccordionTrigger>
              <AccordionContent className="pb-0">
                <EurobonusForm dictionary={dictionary} />
              </AccordionContent>
            </AccordionItem>
          )}
        </Accordion>
      </div>
    </div>
  );
}
